import { Box, Flex, Heading, Icon, Select, SimpleGrid, Text, useColorModeValue } from '@chakra-ui/react'
import { endOfMonth, format, getMonth, getYear } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CalendarIcon, PieChart as PieIcon } from 'lucide-react'
import * as React from 'react'
import { useEffect, useState } from 'react'
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '../../services/api'
import { CardsContainer } from './components/cards'
import OpenAccountsCards from './components/openAccountsCards'
import { PaymentMethodsCard } from './components/paymentMethodsCard'
import { StatusOrdersCard } from './components/statusOrder'
import { DashboardData, OverdueAccountsTableProps, PeriodFilterProps } from './interfaces/dashboard.interface'

const OverdueAccountsTable: React.FC<OverdueAccountsTableProps> = ({ data, totalOverdue, avgDaysOverdue }) => {
  const tableBg = useColorModeValue('gray.700', 'gray.700')
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

  const getDaysOverdueBadge = (days: number) => {
    if (days >= 30) return 'red'
    if (days >= 15) return 'orange'
    return 'gray'
  }

  return (
    <Box borderWidth="1px" borderRadius="lg" p={6} bg={tableBg} shadow="sm">
      <OpenAccountsCards data={data} totalOverdue={totalOverdue} avgDaysOverdue={avgDaysOverdue} />
    </Box>
  )
}

const PeriodFilter: React.FC<PeriodFilterProps> = ({ selectedPeriod, onPeriodChange }) => {
  const currentYear = getYear(new Date())
  const periods = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(currentYear, i, 1)
    return {
      value: `${currentYear}-${String(i + 1).padStart(2, '0')}`,
      label: format(date, 'MMMM yyyy', { locale: ptBR }),
    }
  })

  return (
    <Flex alignItems="center" gap={2}>
      <Icon as={CalendarIcon} w={5} h={5} color="white" />
      <Select
        value={selectedPeriod}
        onChange={(e) => onPeriodChange(e.target.value)}
        color="white"
        sx={{
          option: {
            color: 'black',
          },
        }}>
        {periods.map((period) => (
          <option key={period.value} value={period.value}>
            {period.label}
          </option>
        ))}
      </Select>
    </Flex>
  )
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedPeriod, setSelectedPeriod] = useState(
    `${getYear(new Date())}-${String(getMonth(new Date()) + 1).padStart(2, '0')}`,
  )

  // Chakra UI colors centralizados no topo
  const bgColor = useColorModeValue('gray.800', 'gray.800')
  const headerBgColor = useColorModeValue('gray.700', 'gray.700')
  const textColor = useColorModeValue('white', 'white')
  const gridStroke = useColorModeValue('gray.200', 'gray.600')
  const axisStroke = useColorModeValue('gray.600', 'gray.400')
  const tooltipBg = useColorModeValue('white', 'gray.700')
  const tooltipBorder = useColorModeValue('gray.200', 'gray.600')
  const footerBg = useColorModeValue('gray.100', 'gray.800')

  useEffect(() => {
    let isMounted = true
    const fetchData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const [year, month] = selectedPeriod.split('-')
        const startDate = format(new Date(Number(year), Number(month) - 1, 1), 'yyyy-MM-dd')
        const endDate = format(endOfMonth(new Date(Number(year), Number(month) - 1, 1)), 'yyyy-MM-dd')

        const response = await api.get('/dashboard', {
          params: { start_date: startDate, end_date: endDate },
        })

        if (isMounted) setData(response.data)
      } catch (err) {
        if (isMounted) setError('Falha ao carregar os dados do dashboard.')
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }
    fetchData()
    return () => {
      isMounted = false
    }
  }, [selectedPeriod])

  if (isLoading) return <Box p={8}>Carregando...</Box>
  if (error) return <Box p={8}>{error}</Box>
  if (!data) return <Box p={8}>Nenhum dado encontrado.</Box>

  const isPositiveGrowth = (growth?: string) => !!growth && growth.startsWith('+')

  return (
    <Box bg={bgColor} minH="100vh">
      {/* Header */}
      <Box as="header" borderBottomWidth="1px" bg={headerBgColor}>
        <Flex
          className="container"
          mx="auto"
          px={6}
          py={4}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={4}>
          <Box>
            <Heading as="h1" size="lg" color={textColor}>
              Dashboard Financeiro
            </Heading>
            <Text fontSize="sm" color={textColor} mt={1}>
              Visão geral do desempenho financeiro
            </Text>
          </Box>
          <PeriodFilter selectedPeriod={selectedPeriod} onPeriodChange={setSelectedPeriod} />
        </Flex>
      </Box>

      <Box as="main" className="container" bg={bgColor} mx="auto" px={6} py={8}>
        <CardsContainer data={data} isPositiveGrowth={isPositiveGrowth} />

        {/* Charts Row 1 */}
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mb={8}>
          {/* Monthly Flow Chart */}
          <Box p={6} gridColumn={{ lg: 'span 2' }} borderWidth="1px" borderRadius="lg" bg={headerBgColor}>
            <Heading as="h3" size="md" mb={4}>
              Fluxo Mensal (Receitas x Despesas)
            </Heading>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.monthlyFlow ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis dataKey="month" stroke={axisStroke} />
                <YAxis stroke={axisStroke} />
                <Tooltip
                  contentStyle={{ backgroundColor: tooltipBg, borderColor: tooltipBorder }}
                  formatter={(value: number) => formatCurrency(value)}
                />
                <Legend />
                <Line type="monotone" dataKey="receitas" stroke="#48BB78" strokeWidth={2} name="Receitas" />
                <Line type="monotone" dataKey="despesas" stroke="#F56565" strokeWidth={2} name="Despesas" />
                <Line type="monotone" dataKey="saldo" stroke="#4299E1" strokeWidth={2} name="Saldo" />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Category Distribution */}
          <Box p={6} borderWidth="1px" borderRadius="lg" bg={headerBgColor}>
            <Heading as="h3" size="md" mb={4} display="flex" alignItems="center" gap={2}>
              <Icon as={PieIcon} /> Despesas por Categoria
            </Heading>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.categoryData ?? []}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                  {(data.categoryData ?? []).map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </SimpleGrid>

        {/* Overdue Accounts Table */}
        <Box mb={8} bg={headerBgColor}>
          <OverdueAccountsTable
            data={data.overdueAccountsList ?? []}
            totalOverdue={data.overdueAccounts}
            avgDaysOverdue={data.avgDaysOverdue}
          />
        </Box>

        {/* Charts Row 2 */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} bg={bgColor}>
          {/* Payment Methods */}
          <PaymentMethodsCard
            data={data.paymentMethods}
            headerBgColor={headerBgColor}
            gridStroke={gridStroke}
            axisStroke={axisStroke}
          />

          {/* Service Orders Status */}
          <Box p={6} borderWidth="1px" borderRadius="lg" bg={headerBgColor}>
            <StatusOrdersCard
              data={data.serviceOrders}
              headerBgColor={headerBgColor}
              gridStroke={gridStroke}
              axisStroke={axisStroke}
            />
            <Flex mt={8} p={4} bg={footerBg} borderRadius="lg" justifyContent="space-between" alignItems="center">
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Total de OS
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {(data.serviceOrders ?? []).reduce((acc, item) => acc + item.count, 0)}
                </Text>
              </Box>
              <Box textAlign="right">
                <Text fontSize="sm" color="gray.500">
                  Taxa de Quitação
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="green.500">
                  {(data.serviceOrders ?? []).find((item) => item.status === 'Pagas')?.value ?? 0}%
                </Text>
              </Box>
            </Flex>
          </Box>
        </SimpleGrid>
      </Box>
    </Box>
  )
}
